<template>
  <div class="view-orders-cnt">
    <BannerCarousel class="banner-cnt" :section-type="sectionType" :scroll-target-id="'orders'" />

    <div id="orders" class="view-orders">
      <div class="form">
        <div class="title-cnt">
          <h3 class="page-title" data-cy="orders-title">{{ $t('viewOrders.title') }} {{ totalRows }}</h3>
          <div class="action-buttons">
            <router-link to="/launch-calendar" data-cy="launch-calendar-button">
              <BackstageButton icon-right="calendar">
                {{ $t('viewLaunchCalendar.launch_calendar_button') }}
              </BackstageButton>
            </router-link>
            <router-link :to="{ name: productsAvailabilityRouteName }" data-cy="check-availability-button">
              <BackstageButton>
                {{ $t('viewOrders.check_availability_button') }}
              </BackstageButton>
            </router-link>
            <BackstageButton
              icon-right="addition-sign"
              :disabled="salesOrdersDisabled"
              data-cy="place-new-order-button-cnt"
              data-cy-button="place-new-order-button"
              v-on:click="showOrderCreatePopUp = true">
              {{ $t('viewOrders.place_new_order_button') }}
            </BackstageButton>
          </div>
        </div>
        <div class="nav-pills" data-cy="orders-table-options">
          <nav>
            <a
              v-for="option in viewOptions"
              :key="option"
              class="link"
              :class="{ selected: option === currentViewOption }"
              :data-cy="`viewOrders.viewOptions.${option}`"
              v-on:click="selectViewOption(option)">
              <span>{{ $t(`viewOrders.viewOptions.${option}`) }}</span>
            </a>
          </nav>
          <div class="buttons-menu">
            <BackstageButton
              v-if="shouldShowItems()"
              :disabled="disabledButton"
              :variant="ButtonVariant.SECONDARY"
              :icon-left="downloadingCsv ? 'loading' : 'xlsx-file'"
              data-cy="download-backorders-button-cnt"
              data-cy-button="download-backorders-button"
              v-on:click="downloadBackorderItems()">
              {{ $t('viewOrders.download_backorder') }}
            </BackstageButton>
            <div v-if="isDuplicationFeatureEnabled()">
              <div v-if="!selectionEnabled">
                <BackstageButton
                  v-if="totalRows && !gridLoading"
                  :variant="ButtonVariant.SECONDARY"
                  data-cy="select-to-duplicate-button-cnt"
                  data-cy-button="select-to-duplicate-button"
                  v-on:click="enableRowSelection">
                  {{ $t('global.select_button') }}
                </BackstageButton>
              </div>
              <div v-else>
                <div class="buttons-menu" v-if="totalRows && !gridLoading">
                  <router-link :to="'/orders-duplication'">
                    <BackstageButton
                      :variant="ButtonVariant.SECONDARY"
                      :disabled="!orderDuplicationEnabled"
                      data-cy="duplicate-order-button-cnt"
                      data-cy-button="duplicate-order-button">
                      {{ $t('viewOrders.duplicate_order_button') }}
                    </BackstageButton>
                  </router-link>
                  <BackstageButton
                    :variant="ButtonVariant.SECONDARY"
                    data-cy="cancel-button-cnt"
                    data-cy-button="cancel-button"
                    v-on:click="disableRowSelection()">
                    {{ $t('global.cancel_button') }}
                  </BackstageButton>
                </div>
              </div>
            </div>
            <BackstageButton
              v-if="totalRows"
              :variant="ButtonVariant.SECONDARY"
              icon-left="trash"
              data-cy="clear-all-filters-button-cnt"
              data-cy-button="clear-all-filters-button"
              v-on:click="clearGridFilters()">
              {{ $t('global.grids.clear_all_filters_button') }}
            </BackstageButton>
          </div>
        </div>
        <OrdersGrid
          v-if="shouldShowOrders()"
          ref="orders"
          :draft-id="draftId"
          :order-id="orderId"
          :selection-enabled="selectionEnabled"
          v-on:on-selection-changed="onSelectionChanged"
          v-on:update-row-count="updateTotalRows"
          v-on:grid-filter-update="updateOrderOverview"
          v-on:grid-loading-done="gridLoaded()" />
        <UnshippedItemsGrid
          v-if="shouldShowItems()"
          ref="items"
          v-on:update-row-count="updateTotalRows"
          v-on:backorder-items-loaded="enableBackorderDownloadButton" />
        <NotificationMessage
          v-if="showOrderDuplicateRestrictionPopUp"
          :size="orderDuplicateRestrictionPopUpSize"
          :type="orderDuplicateRestrictionPopUpType"
          v-on:close="closeOrderDuplicateRestrictionPopUp">
          <template v-slot:default>
            <div v-html="t('viewOrders.order_duplication.orders_restriction_note')"></div>
          </template>
        </NotificationMessage>
      </div>
    </div>
    <OrderOverview
      v-if="shouldShowOrders()"
      :from="orderOverview.from"
      :to="orderOverview.to"
      :total-items="orderOverview.totalItems"
      :filter-type="orderOverview.filterType"
      :order-numbers="orderOverview.orderNumbers" />

    <div v-if="showOrderCreatePopUp">
      <NotificationMessage
        :title="$t('createOrdersPopup.order_types_title')"
        :size="'medium'"
        v-on:close="showOrderCreatePopUp = false">
        <InitNewOrder v-on:close="showOrderCreatePopUp = false" />
      </NotificationMessage>
    </div>
  </div>
</template>

<script lang="ts">
import BackstageButton from '@/components/common/BackstageButton.vue';
import { utilsMixins } from '@/mixins/utils.mixins';
import { Feature } from '@/models/feature.model';
import { PageEnum } from '@/models/page.enum';
import { PopUpSize } from '@/models/pop-up-size.enum';
import { PopUpType } from '@/models/pop-up-type.enum';
import { OrderItemsService } from '@/services/order-items.service';
import { useSelectedOrdersStore } from '@/stores/selected-grid-orders';
import { useUserStore } from '@/stores/user';
import { storeToRefs } from 'pinia';
import { defineComponent } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute, useRouter } from 'vue-router';
import { GaCustomEvents } from '../../models/ga-custom-events.enum';
import { OrderOverviewModel } from '../../models/order-overview.model';
import { RouteNames } from '../../router';
import { FeaturesService } from '../../services/features.service';
import NotificationMessage from '../common/NotificationMessage.vue';
import BannerCarousel from '../common/banner-carousel.vue';
import InitNewOrder from './init-new-order.vue';
import OrderOverview from './order-overview.vue';
import OrdersGrid from './orders-grid.vue';
import UnshippedItemsGrid from './unshipped-items-grid.vue';
import { useDownload } from '@/composables/download';
import { ButtonVariant } from '@/models/button-variant.enum';

enum ViewOptions {
  Orders = 'orders',
  Items = 'items',
}

const ORDERS_DUPLICATION_LIMIT = 10;

export default defineComponent({
  name: 'view-orders',
  components: {
    OrdersGrid,
    UnshippedItemsGrid,
    BannerCarousel,
    OrderOverview,
    BackstageButton,
    NotificationMessage,
    InitNewOrder,
  },
  props: {
    orderId: {
      type: String,
      default: undefined,
    },
    draftId: {
      type: String,
      default: undefined,
    },
    show: {
      type: String,
      default: ViewOptions.Orders,
    },
  },
  setup() {
    const route = useRoute();
    const router = useRouter();
    const { selectedAccountId, draftInitInfo } = storeToRefs(useUserStore());
    const { selectedOrders, removeSelectedOrders } = useSelectedOrdersStore();
    const { t } = useI18n();
    const { downloadXlsxFile } = useDownload();

    return {
      route,
      router,
      accountId: selectedAccountId,
      selectedOrders,
      draftInitInfo,
      ButtonVariant,
      t,
      removeSelectedOrders,
      downloadXlsxFile,
    };
  },
  data() {
    return {
      sectionType: PageEnum.homePage,
      selectedOrderId: undefined as any,
      selectedOrderIsDraft: false,
      salesOrdersDisabled: true,
      featuresService: new FeaturesService(),
      viewOptions: Object.values(ViewOptions),
      currentViewOption: this.show,
      totalRows: '',
      orderItemsService: new OrderItemsService(),
      disabledButton: true,
      downloadingCsv: false,
      showOrderCreatePopUp: false,
      orderOverview: new OrderOverviewModel(),
      productsAvailabilityRouteName: RouteNames.ProductsAvailabilityDefault,
      orderDuplicationEnabled: false,
      selectionEnabled: false,
      showOrderDuplicateRestrictionPopUp: false,
      orderDuplicateRestrictionPopUpSize: PopUpSize.Small,
      orderDuplicateRestrictionPopUpType: PopUpType.Warning,
      selectedRows: new Array<any>(),
      gridLoading: true,
    };
  },
  watch: {
    accountId(newVal) {
      if (newVal) {
        this.gridLoading = true;
        this.determineWhetherSalesOrdersAreDisabled(newVal);
      }
    },
  },
  beforeMount() {
    this.currentViewOption = this.show;
  },
  mounted() {
    this.removeSelectedOrders();
    this.determineWhetherSalesOrdersAreDisabled(this.accountId as string);

    if (this.orderId) {
      this.selectedOrderId = this.orderId;
    }

    if (this.draftId) {
      this.selectedOrderId = this.draftId;
      this.selectedOrderIsDraft = true;
    }
  },
  methods: {
    ...utilsMixins,
    async determineWhetherSalesOrdersAreDisabled(accountId: string) {
      this.salesOrdersDisabled = await this.featuresService.hasFeature(Feature.SALES_ORDERS_DISABLED, accountId);
    },
    gridLoaded() {
      this.gridLoading = false;
    },
    isDuplicationFeatureEnabled() {
      return (
        import.meta.env.VITE_APP_orderDuplicationEnabled === 'true' && this.currentViewOption === ViewOptions.Orders
      );
    },
    closeOrderDuplicateRestrictionPopUp() {
      this.showOrderDuplicateRestrictionPopUp = false;
    },
    onSelectionChanged(selectedRows: any[]) {
      this.orderDuplicationEnabled = selectedRows.length > 0 && selectedRows.length <= ORDERS_DUPLICATION_LIMIT;

      if (selectedRows.length > this.selectedRows.length && selectedRows.length > ORDERS_DUPLICATION_LIMIT) {
        this.showOrderDuplicateRestrictionPopUp = true;
      }
      this.selectedRows = selectedRows;
    },
    enableRowSelection() {
      this.selectionEnabled = true;
    },
    disableRowSelection() {
      this.selectionEnabled = false;
    },
    shouldShowOrders() {
      return this.currentViewOption === ViewOptions.Orders;
    },
    shouldShowItems() {
      return this.currentViewOption === ViewOptions.Items;
    },
    selectViewOption(option: string) {
      this.currentViewOption = option;

      if (this.route.query.show !== this.currentViewOption) {
        this.totalRows = '';
        this.router.replace({ query: { ...this.route.query, show: this.currentViewOption } });
        this.attemptAutoScroll('orders', this.route.query);
      }
    },
    updateTotalRows(rowCount: number) {
      this.totalRows = `( ${rowCount} ${this.$t('viewOrders.title-suffix')} )`;
    },
    async downloadBackorderItems() {
      this.disabledButton = true;
      this.downloadingCsv = true;

      await this.orderItemsService.downloadBackorderItems(this.accountId).then((response) => {
        if (response.isValid()) {
          try {
            this.downloadXlsxFile(response.model?.base64Body, `${this.accountId}`);
          } catch (err) {
            this.$snotify.warning(this.$t('documentsCheckout.document_is_not_available'));

            this.addCustomGaEvent({
              event: GaCustomEvents.FileDownloadError,
              link_text: this.accountId,
            });
          }
        }
      });

      this.disabledButton = false;
      this.downloadingCsv = false;
    },
    enableBackorderDownloadButton(backorderItemsLoaded: boolean) {
      this.disabledButton = !backorderItemsLoaded;
    },
    clearGridFilters() {
      const gridElement: any = this.$refs[this.currentViewOption];

      gridElement.removeAllGridFilters();
      this.orderOverview = new OrderOverviewModel();
    },
    updateOrderOverview(overview: OrderOverviewModel) {
      this.orderOverview = overview;
    },
  },
});
</script>
<style scoped lang="scss">
.view-orders-cnt {
  .banner-cnt {
    margin: auto;
  }

  .view-orders {
    @include common-container;
    min-height: 100vh;
    padding-top: 5px;

    .title-cnt {
      display: flex;
      align-items: center;
      justify-content: space-between;
      gap: 10px;
      flex-wrap: wrap;

      .action-buttons {
        display: flex;
        gap: 10px;
      }
    }

    .form {
      @include container-background;
    }

    .nav-pills .buttons-menu {
      display: flex;
      gap: 10px;
      flex-wrap: wrap;
    }
  }
  .selector-button-box {
    display: flex;
  }
  .button {
    margin-top: 7px;
  }
  .button-negative {
    &::before {
      content: none;
    }
  }
  .xlsx-file-button-backorder {
    background-size: 2em;
    background-position-x: 1em;
    float: left;
    height: 5%;
  }
}
</style>
@/stores/selected-grid-orders
